// SPDX-License-Identifier: Apache-2.0

import { utils } from "ethers";

// Base-unit decimals. CKB amounts are shannons (1e8), ETH amounts are wei
// (1e18). Shares are wei-scale too — see lpFormatShares.
export const CKB_DECIMALS = 8;
export const ETH_DECIMALS = 18;

// Hard capacity floor for a CKB LP cell: 343 CKB = 34_300_000_000 shannons
// (perun-ckb-backend/adapter/ckb_lp/encoding.go). Deposits below this are
// rejected by the builder, withdrawals may never take the cell below it, and
// there is no close-cell operation that reclaims it — it is the cell's state
// rent for life. It was 323 CKB until the cell data grew from 185 to 205
// bytes to carry eth_beneficiary.
export const MIN_CKB_DEPOSIT_SHANNONS = BigInt("34300000000");

// Flat fee the LP withdraw builder pays out of the cell's available CKB
// (perun-ckb-backend transaction.DefaultFeeShannon = 0.0001 CKB).
export const LP_WITHDRAW_FEE_SHANNONS = BigInt(10000);

// Converts a base-unit decimal string (shannons/wei) to a human string.
export function lpToHuman(base: string, decimals: number): string {
  try {
    return utils.formatUnits(base || "0", decimals);
  } catch {
    return "0";
  }
}

// Converts a human amount to a base-unit decimal string. Throws on bad input.
export function lpFromHuman(human: string, decimals: number): string {
  return utils.parseUnits(human.trim(), decimals).toString();
}

// ETH-pool shares are wei-scale: the pool's first deposit mints 1 share per
// wei (LiquidityPool._toShares), so 1e18 shares ≈ 1 ETH of principal at
// launch. Display them scaled like ETH; the raw value stays the wire format.
export function lpFormatShares(shares: string): string {
  return lpToHuman(shares || "0", 18);
}

// The CKB a cell owner can pull out right now: available minus the cell's
// permanent 343 CKB state rent and the flat withdraw tx fee, clamped at
// zero. Shannons in, shannons out (decimal strings).
export function lpMaxWithdrawShannons(availableCKB: string): string {
  try {
    const max =
      BigInt(availableCKB || "0") -
      MIN_CKB_DEPOSIT_SHANNONS -
      LP_WITHDRAW_FEE_SHANNONS;
    return max > BigInt(0) ? max.toString() : "0";
  } catch {
    return "0";
  }
}

// Shortens a 0x address for table display, keeping enough of both ends to be
// checkable by eye. Short or empty values are returned unchanged.
export function lpTruncateAddress(addr: string): string {
  if (!addr || addr.length <= 13) {
    return addr || "—";
  }
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

// Owner pool fraction as a percentage string, computed with BigInt to avoid
// overflow. Returns "0" when the pool has no shares.
export function lpPoolSharePct(shares: string, totalShares: string): string {
  try {
    const total = BigInt(totalShares || "0");
    if (total === BigInt(0)) {
      return "0";
    }
    const bps = (BigInt(shares || "0") * BigInt(10000)) / total;
    return (Number(bps) / 100).toString();
  } catch {
    return "0";
  }
}
